import React, { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { GuidService } from 'ui-components/services/guid-service';

import defaultFilterSet from './constants/default-new-filter';
import defaultNumericFilter from './constants/default-numeric-filter';
import { removeAppliedFilterByWorkingFilterId } from './helpers/adjusted-filters-for-commit';
import { buildFilterSetForSave, generateUpdatedAppliedFilters } from './helpers/filter-creation-helpers';
import Global from '../../store/global-selectors';
import { getFilterSets, updateFilterSet, postFilterSet, deleteFilterSet } from '../../store/saved-filters-api';

export type AppliedFilter = {
  column: string;
  workingFilterId?: string;
  operator?: string;
  isIn?: boolean;
  isNull?: boolean;
  value?: string;
  values?: string[];
  tempSortId?: number;
};

export type AppliedFilters = {
  [key: string]: AppliedFilter[] | undefined; // To enable: appliedFilters[category]
  numericFilters?: AppliedFilter[];
  booleanFilters?: AppliedFilter[];
  collectionFilters?: AppliedFilter[];
  dateTimeFilters?: AppliedFilter[];
  stringFilters?: AppliedFilter[];
};

export type FilterSet = {
  orderFilterSetId?: string | number;
  name?: string;
  description?: string | null;
  active?: boolean;
  modifyDate?: string;
  appliedFilters?: AppliedFilters;
};

type PreconfiguredFilter = {
  column: string;
  operator: string;
  value: number;
};

type FilterEditorContextValue = {
  filterSets: Record<string, FilterSet>;
  isLoadingFilters: boolean;
  setShouldFetchFilters: (should: boolean) => void;
  filterSet: FilterSet;
  updateAppliedFilter: (appliedFilter: AppliedFilter) => void;
  saveFilterSet: (onSuccessAction?: (filterId: string) => void) => void;
  addNewDefaultFilter: () => void;
  filterName: string | undefined;
  setFilterName: (name: string | undefined) => void;
  filterDescription: string | undefined;
  setFilterDescription: (desc: string | undefined) => void;
  removeFilter: (workingFilterId: string) => void;
  setFilterSetWithWorkingFilterId: (filterSet: FilterSet | null | undefined) => void;
  getNewTemporaryFilter: () => string;
  onDeleteFilterSet: (orderFilterSetId: string) => void;
  makeFilterActiveOnSave: boolean;
  setMakeFilterActiveOnSave: (value: boolean) => void;
  preconfiguredFilter: PreconfiguredFilter | null | undefined;
};

const FilterEditorContext = React.createContext<FilterEditorContextValue>({} as FilterEditorContextValue);

type FilterEditorContextProviderProps = {
  children: React.ReactNode;
  filterId?: string;
  onAddNewCustomAllocationRule?: (orderFilterSetId: string | number, filterName: string | undefined) => void;
  preconfiguredFilter?: PreconfiguredFilter | null;
};

const FilterEditorContextProvider = ({
  children,
  filterId,
  onAddNewCustomAllocationRule = noop,
  preconfiguredFilter = null,
}: FilterEditorContextProviderProps) => {
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const [_filterId, setFilterId] = useAsyncSafeState(filterId);
  const [shouldFetchFilters, setShouldFetchFilters] = useAsyncSafeState(true);
  const [isLoadingFilters, setIsLoadingFilters] = useAsyncSafeState(true);
  const [filterSets, setFilterSets] = useAsyncSafeState({});
  const [filterSet, setFilterSet] = useAsyncSafeState(defaultFilterSet);
  const [filterName, setFilterName] = useAsyncSafeState();
  const [filterDescription, setFilterDescription] = useAsyncSafeState();
  const [makeFilterActiveOnSave, setMakeFilterActiveOnSave] = useState(false);

  const onDeleteFilterSet = useCallback(
    (orderFilterSetId: string) => {
      deleteFilterSet(orderFilterSetId).then(() => {
        setShouldFetchFilters(true);
      });
    },
    [setShouldFetchFilters],
  );

  const setFilterSetWithWorkingFilterId = useCallback(
    (filterSetToEdit: FilterSet | null | undefined) => {
      if (!filterSetToEdit) {
        return;
      }
      const { appliedFilters } = filterSetToEdit;
      if (!appliedFilters) {
        return;
      }
      const appliedFiltersWithWorkingId = Object.keys(appliedFilters).reduce((acc: AppliedFilters, filterCategory: string) => {
        return {
          ...acc,
          [filterCategory]: (appliedFilters[filterCategory] ?? []).map((f) => ({
            ...f,
            workingFilterId: GuidService.newGuid(),
          })),
        };
      }, {});
      setFilterSet({
        ...filterSetToEdit,
        appliedFilters: appliedFiltersWithWorkingId,
      });
      setFilterId(filterSetToEdit.orderFilterSetId);
      setFilterName(filterSetToEdit.name);
      setFilterDescription(filterSetToEdit.description);
    },
    [setFilterSet, setFilterId, setFilterName, setFilterDescription],
  );

  useEffect(() => {
    if (shouldFetchFilters) {
      setIsLoadingFilters(true);
      getFilterSets().then((filterSetResponse) => {
        setFilterSets(filterSetResponse);
        setShouldFetchFilters(false);
        setIsLoadingFilters(false);
        if (_filterId) {
          const filterToEdit = filterSetResponse[_filterId];
          setFilterName(filterToEdit?.name);
          setFilterDescription(filterToEdit?.description);
          setFilterSetWithWorkingFilterId(filterToEdit);
        }
      });
    }
  }, [
    setFilterSets,
    shouldFetchFilters,
    setShouldFetchFilters,
    _filterId,
    setFilterSetWithWorkingFilterId,
    setIsLoadingFilters,
    setFilterName,
    setFilterDescription,
  ]);

  const updateAppliedFilter = useCallback(
    (appliedFilter: AppliedFilter) => {
      const { appliedFilters } = filterSet;
      const updatedAppliedFilters = generateUpdatedAppliedFilters(appliedFilter, appliedFilters);
      setFilterSet({
        ...filterSet,
        name: filterName,
        appliedFilters: updatedAppliedFilters,
      });
    },
    [filterSet, setFilterSet, filterName],
  );

  const saveFilterSet = useCallback(
    (onSuccessAction?: (filterId: string) => void) => {
      setIsLoadingFilters(true);
      const hasAlreadySavedFilter = _filterId && !GuidService.isGuid(_filterId?.toString());
      const filterSetForUpdate = {
        ...buildFilterSetForSave({
          filterSet,
          sellerHomeCountry,
          filterName,
          filterDescription,
          active: makeFilterActiveOnSave ? true : filterSet.active,
        }),
        orderFilterSetId: hasAlreadySavedFilter ? _filterId : undefined,
      };
      if (!hasAlreadySavedFilter) {
        postFilterSet(filterSetForUpdate).then((response) => {
          const { orderFilterSetId } = response;
          setFilterId(orderFilterSetId);

          if (onAddNewCustomAllocationRule) {
            onAddNewCustomAllocationRule(orderFilterSetId, filterName);
          }
          if (onSuccessAction) {
            onSuccessAction(orderFilterSetId);
          }
        });
        setShouldFetchFilters(true);
        return;
      }
      updateFilterSet(filterSetForUpdate).then(() => {
        if (onSuccessAction) {
          onSuccessAction(_filterId);
        }
        setShouldFetchFilters(true);
      });
    },
    [
      setIsLoadingFilters,
      filterSet,
      setFilterId,
      onAddNewCustomAllocationRule,
      filterName,
      filterDescription,
      sellerHomeCountry,
      _filterId,
      setShouldFetchFilters,
      makeFilterActiveOnSave,
    ],
  );

  const addNewDefaultFilter = useCallback(() => {
    const totalAppliedFilters = Object.keys(filterSet.appliedFilters).reduce((acc, filterCategory) => {
      return acc + (filterSet.appliedFilters[filterCategory] || []).length;
    }, 0);
    const filterToDisplay = preconfiguredFilter || defaultNumericFilter;
    const newFilterSet = {
      ...filterSet,
      appliedFilters: {
        ...filterSet.appliedFilters,
        numericFilters: [
          ...(filterSet.appliedFilters.numericFilters || []),
          {
            ...filterToDisplay,
            workingFilterId: GuidService.newGuid(),
            tempSortId: totalAppliedFilters + 1,
          },
        ],
      },
    };
    setFilterSet(newFilterSet);
  }, [filterSet, preconfiguredFilter, setFilterSet]);

  const removeFilter = useCallback(
    (workingFilterId: string) => {
      const { appliedFilters } = filterSet;
      const newAppliedFilters = removeAppliedFilterByWorkingFilterId(appliedFilters, workingFilterId);
      setFilterSet({
        ...filterSet,
        appliedFilters: newAppliedFilters,
      });
    },
    [filterSet, setFilterSet],
  );

  const getNewTemporaryFilter = useCallback((): string => {
    const newFilterId = GuidService.newGuid();
    setFilterSets((prev: Record<string, FilterSet>) => ({
      ...prev,
      [newFilterId]: {
        active: false,
      },
    }));
    return newFilterId;
  }, [setFilterSets]);

  const filterEditorContextValue: FilterEditorContextValue = {
    filterSets,
    isLoadingFilters,
    setShouldFetchFilters,
    filterSet,
    updateAppliedFilter,
    saveFilterSet,
    addNewDefaultFilter,
    filterName,
    setFilterName,
    filterDescription,
    setFilterDescription,
    removeFilter,
    setFilterSetWithWorkingFilterId,
    getNewTemporaryFilter,
    onDeleteFilterSet,
    makeFilterActiveOnSave,
    setMakeFilterActiveOnSave,
    preconfiguredFilter,
  };

  return <FilterEditorContext.Provider value={filterEditorContextValue}>{children}</FilterEditorContext.Provider>;
};

export { FilterEditorContext, FilterEditorContextProvider };
